import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { VinculadorPagadorPmService } from '../../services/vinculador-pagador-pm/vinculador-pagador-pm.service';
import { LoadingService } from '../../services/general/loading.service';

type UploadSlot = 'camara' | 'estados';

@Component({
  selector: 'app-vinculador-pagador-pm',
  standalone: true,
  imports: [CommonModule, PageHeaderComponent],
  templateUrl: './vinculador-pagador-pm.component.html',
  styleUrl: './vinculador-pagador-pm.component.css'
})
export class VinculadorPagadorPmComponent {
  @ViewChild('camaraInput') camaraInput!: ElementRef<HTMLInputElement>;
  @ViewChild('estadosInput') estadosInput!: ElementRef<HTMLInputElement>;

  camaraFile: File | null = null;
  estadosFile: File | null = null;
  camaraDragging = false;
  estadosDragging = false;
  isSubmitting = false;
  showAlertError = false;
  showAlertSuccess = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private readonly _service: VinculadorPagadorPmService,
    private readonly _loadingService: LoadingService
  ) {}

  get canSubmit(): boolean {
    return !!this.camaraFile && !!this.estadosFile && !this.isSubmitting;
  }

  onFileSelected(event: Event, slot: UploadSlot): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.processFile(input.files[0], slot);
    }
  }

  onDragOver(event: DragEvent, slot: UploadSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragging(slot, true);
  }

  onDragLeave(event: DragEvent, slot: UploadSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragging(slot, false);
  }

  onDrop(event: DragEvent, slot: UploadSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragging(slot, false);

    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.processFile(files[0], slot);
    }
  }

  triggerFileInput(slot: UploadSlot): void {
    const input = this.getInputRef(slot);
    input?.nativeElement.click();
  }

  clearFile(slot: UploadSlot): void {
    if (slot === 'camara') {
      this.camaraFile = null;
    } else {
      this.estadosFile = null;
    }
    this.resetInput(slot);
  }

  clearAll(): void {
    this.camaraFile = null;
    this.estadosFile = null;
    this.resetInput('camara');
    this.resetInput('estados');
  }

  submit(): void {
    if (!this.camaraFile || !this.estadosFile) {
      this.showError('Debes adjuntar camara de comercio y estados financieros.');
      return;
    }
    if (!this.isPdf(this.camaraFile) || !this.isPdf(this.estadosFile)) {
      this.showError('Solo se permiten archivos PDF.');
      return;
    }

    const email = localStorage.getItem('userName') ?? '';
    if (!email) {
      this.showError('No se pudo identificar al usuario. Vuelve a iniciar sesion.');
      return;
    }

    this.isSubmitting = true;
    this.showAlertError = false;
    this.showAlertSuccess = false;
    this._loadingService.setLoading(true);

    this._service.uploadDocuments(this.camaraFile, this.estadosFile, email).subscribe({
      next: (response) => {
        const backendMessage = response?.message?.trim();
        this.successMessage = backendMessage
          ? `${backendMessage} Recibiras un correo con el link al caso en ProcessMaker cuando el proceso termine. Si los documentos pertenecen a clientes distintos, te avisaremos por correo del error.`
          : 'Documentos recibidos. Estamos procesando la informacion y te enviaremos un correo con el resultado en pocos minutos.';
        this.showAlertSuccess = true;
        this.hideAlertAfterDelay('success');
        this.clearAll();
        this.finishSubmit();
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.mapErrorMessage(error);
        this.showAlertError = true;
        this.hideAlertAfterDelay('error');
        this.finishSubmit();
      }
    });
  }

  formatFileSize(file: File | null): string {
    if (!file) return '';
    const sizeInMb = file.size / (1024 * 1024);
    if (sizeInMb >= 1) {
      return `${sizeInMb.toFixed(2)} MB`;
    }
    const sizeInKb = file.size / 1024;
    return `${sizeInKb.toFixed(0)} KB`;
  }

  private processFile(file: File, slot: UploadSlot): void {
    if (!this.isPdf(file)) {
      this.showError('Solo se permiten archivos PDF.');
      this.clearFile(slot);
      this.resetInput(slot);
      return;
    }

    if (slot === 'camara') {
      this.camaraFile = file;
    } else {
      this.estadosFile = file;
    }
  }

  private isPdf(file: File): boolean {
    const fileName = file.name.toLowerCase();
    return file.type === 'application/pdf' || fileName.endsWith('.pdf');
  }

  private setDragging(slot: UploadSlot, value: boolean): void {
    if (slot === 'camara') {
      this.camaraDragging = value;
    } else {
      this.estadosDragging = value;
    }
  }

  private getInputRef(slot: UploadSlot): ElementRef<HTMLInputElement> | null {
    return slot === 'camara' ? this.camaraInput ?? null : this.estadosInput ?? null;
  }

  private resetInput(slot: UploadSlot): void {
    const input = this.getInputRef(slot);
    if (input) {
      input.nativeElement.value = '';
    }
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.showAlertError = true;
    this.hideAlertAfterDelay('error');
  }

  private hideAlertAfterDelay(type: 'error' | 'success'): void {
    const delay = type === 'success' ? 15000 : 4000;
    setTimeout(() => {
      if (type === 'error') {
        this.showAlertError = false;
      } else {
        this.showAlertSuccess = false;
      }
    }, delay);
  }

  private finishSubmit(): void {
    this.isSubmitting = false;
    this._loadingService.setLoading(false);
  }

  private mapErrorMessage(error: HttpErrorResponse): string {
    const backendMessage = error?.error?.message;

    switch (error?.status) {
      case 400:
        return backendMessage || 'Verifica los archivos: deben ser PDF y pesar menos de 10MB.';
      case 401:
        return 'Tu sesion expiro. Vuelve a iniciar sesion.';
      case 502:
        return 'Servicio no disponible, intenta mas tarde.';
      case 500:
        return 'Ocurrio un error interno. Intenta nuevamente en unos minutos.';
      default:
        return backendMessage || error?.message || 'No fue posible enviar los documentos.';
    }
  }

}
